import { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Star } from "lucide-react";
import "./WorkThread.css";

export type WorkThreadAttempt = {
  submissionId: string;
  attempt: number;
  submittedAt: string | null;
  isPrimary: boolean;
  gradingStatus?: string | null;
};

/** Tag a message with an attempt only when that attempt is on this thread. */
export function messageSubmissionId(tab: string, attempts?: WorkThreadAttempt[] | null) {
  if (!tab || tab === "all") return null;
  return (attempts || []).some((row) => row.submissionId === tab) ? tab : null;
}

export type WorkThreadEntry = {
  id: string;
  kind: "message" | "event" | "announcement" | string;
  eventType?: string | null;
  body: string;
  meta?: Record<string, unknown>;
  questionId?: string | null;
  submissionId?: string | null;
  authorRole: "learner" | "instructor" | "parent" | "system" | string;
  authorName?: string | null;
  mine?: boolean;
  createdAt: string;
};

export type WorkThreadData = {
  progressId?: string | null;
  audience: "public" | "staff" | "personal" | string;
  audiences?: string[];
  canReply: boolean;
  activityStudentMayMessage?: boolean;
  attempts: WorkThreadAttempt[];
  entries: WorkThreadEntry[];
};

const CHANNELS: Record<string, { label: string; kicker?: string; hint: string }> = {
  public: {
    label: "Student",
    kicker: "+ activity",
    hint: "Marks, question comments, handed-in, and chat the student can see.",
  },
  staff: {
    label: "Staff",
    hint: "Only people who can open this paper. The student never sees this.",
  },
  personal: {
    label: "Notes",
    hint: "Private to you on this student’s work.",
  },
};

function authorLabel(entry: WorkThreadEntry) {
  if (entry.mine) return "You";
  if (entry.authorRole === "parent") {
    return entry.authorName ? `${entry.authorName} · Parent` : "Parent";
  }
  if (entry.authorRole === "counselor") {
    return entry.authorName ? `${entry.authorName} · Counselor` : "Counselor";
  }
  return entry.authorName || (entry.authorRole === "learner" ? "Student" : "Teacher");
}

function whenLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return then.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function eventCopy(entry: WorkThreadEntry) {
  if (entry.body) return entry.body;
  switch (entry.eventType) {
    case "submitted":
      return "Handed in";
    case "marked":
      return "Marked";
    case "comment":
      return "Comment";
    case "primary":
      return "This attempt now counts";
    case "extra_attempt":
      return "Attempt limit changed";
    default:
      return "Update";
  }
}

function metaNumber(meta: Record<string, unknown> | undefined, key: string) {
  const n = Number(meta?.[key]);
  return Number.isFinite(n) ? n : null;
}

function eventScoreLabel(entry: WorkThreadEntry) {
  const possible = metaNumber(entry.meta, "pointsPossible");
  const earned = metaNumber(entry.meta, "pointsEarned");
  if (possible == null && earned == null) return null;
  return `${earned == null ? "—" : earned} / ${possible ?? "—"}`;
}

function clipNote(text: string, max = 72) {
  const compact = text.replace(/\s+/g, " ").trim();
  if (compact.length <= max) return { preview: compact, clipped: compact !== text.trim() };
  const cut = compact.slice(0, max).replace(/\s+\S*$/, "").trimEnd();
  return { preview: `${cut || compact.slice(0, max)}…`, clipped: true };
}

function ThreadEvent({
  entry,
  onJumpQuestion,
}: {
  entry: WorkThreadEntry;
  onJumpQuestion?: (questionId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const isComment = entry.eventType === "comment";
  const note = isComment ? (entry.body || "").trim() : "";
  const clipped = note ? clipNote(note) : { preview: "", clipped: false };
  const score = eventScoreLabel(entry);
  const badge = isComment ? (entry.questionId ? "Question" : "Grade feedback") : null;

  return (
    <article className="al-work-thread-item al-work-thread-item--event">
      <p className="al-work-thread-event">
        {badge ? <span className="al-work-thread-badge">{badge}</span> : null}
        {isComment && score ? <span className="al-work-thread-event-score">{score}</span> : null}
        {!isComment ? <span>{eventCopy(entry)}</span> : null}
        {entry.questionId && onJumpQuestion ? (
          <button
            type="button"
            className="al-work-thread-jump"
            onClick={() => onJumpQuestion(entry.questionId as string)}
          >
            Open
          </button>
        ) : null}
        <time dateTime={entry.createdAt}>{whenLabel(entry.createdAt)}</time>
      </p>
      {note ? (
        clipped.clipped ? (
          <button
            type="button"
            className={`al-work-thread-event-note${open ? " is-open" : ""}`}
            aria-expanded={open}
            onClick={() => setOpen((on) => !on)}
          >
            {open ? note : clipped.preview}
          </button>
        ) : (
          <p className="al-work-thread-event-note is-open">{note}</p>
        )
      ) : null}
    </article>
  );
}

export default function WorkThread({
  data,
  tab,
  onTabChange,
  audience,
  onAudienceChange,
  onSend,
  onJumpQuestion,
  sending = false,
  channels = false,
  channelMeta,
  closedHint,
  emptyText,
  placeholder,
}: {
  data: WorkThreadData | null;
  tab: string;
  onTabChange: (tab: string) => void;
  audience: string;
  onAudienceChange?: (audience: string) => void;
  onSend: (body: string) => Promise<void> | void;
  onJumpQuestion?: (questionId: string) => void;
  sending?: boolean;
  channels?: boolean;
  channelMeta?: Record<string, { label?: string; kicker?: string; hint?: string }>;
  /** Replaces the default line shown when this viewer cannot write. */
  closedHint?: string;
  /** Replaces the activity empty line. Used by the student room. */
  emptyText?: string;
  /** Replaces the activity compose placeholder. */
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  const scroller = useRef<HTMLDivElement>(null);
  const attempts = data?.attempts || [];
  const entries = useMemo(() => {
    const rows = data?.entries || [];
    if (tab === "all") return rows;
    return rows.filter((row) => String(row.submissionId || "") === tab);
  }, [data?.entries, tab]);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [entries.length, tab, data?.audience]);

  const canCompose = Boolean(data?.canReply);
  const channelsList = (data?.audiences || ["public"]).filter((id) => CHANNELS[id]);
  const channel = { ...(CHANNELS[audience] || CHANNELS.public), ...(channelMeta?.[audience] || {}) };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending || !canCompose) return;
    await onSend(text);
    setDraft("");
  };

  return (
    <div className="al-work-thread">
      {channels && onAudienceChange && channelsList.length > 1 ? (
        <>
          <div className="al-work-thread-channels" role="tablist" aria-label="Channel">
            {channelsList.map((id) => {
              const spec = { ...CHANNELS[id], ...(channelMeta?.[id] || {}) };
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={audience === id}
                  className={`al-work-thread-channel${audience === id ? " is-on" : ""}`}
                  onClick={() => onAudienceChange(id)}
                >
                  <span className="al-work-thread-channel-label">{spec.label}</span>
                  {spec.kicker ? (
                    <span className="al-work-thread-channel-kicker">{spec.kicker}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <p className="al-work-thread-channel-hint">{channel.hint}</p>
        </>
      ) : null}

      {attempts.length > 0 ? (
      <div className="al-work-thread-tabs" role="tablist" aria-label="Attempts">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "all"}
          className={`al-work-thread-tab${tab === "all" ? " is-on" : ""}`}
          onClick={() => onTabChange("all")}
        >
          All
        </button>
        {attempts.map((row) => (
          <button
            key={row.submissionId}
            type="button"
            role="tab"
            aria-selected={tab === row.submissionId}
            className={`al-work-thread-tab${tab === row.submissionId ? " is-on" : ""}`}
            onClick={() => onTabChange(row.submissionId)}
          >
            Attempt {row.attempt}
            {row.isPrimary ? <Star size={10} aria-label="Counts" /> : null}
          </button>
        ))}
      </div>
      ) : null}

      <div className="al-work-thread-log" ref={scroller}>
        {entries.length === 0 ? (
          <p className="al-work-thread-empty">
            {emptyText
              ? emptyText
              : tab === "all"
              ? audience === "staff"
                ? "No staff messages yet."
                : audience === "personal"
                  ? "No notes yet."
                  : "Nothing on this work yet."
              : audience === "staff"
                ? "Nothing on this try yet."
                : audience === "personal"
                  ? "No notes on this try yet."
                  : "Nothing on this try yet."}
          </p>
        ) : (
          entries.map((entry) =>
            entry.kind === "event" ? (
              <ThreadEvent key={entry.id} entry={entry} onJumpQuestion={onJumpQuestion} />
            ) : (
              <article
                key={entry.id}
                className={`al-work-thread-item al-work-thread-item--${entry.authorRole}${entry.mine ? " is-mine" : ""}`}
              >
                <header className="al-work-thread-who">
                  <span>{authorLabel(entry)}</span>
                  <time dateTime={entry.createdAt}>{whenLabel(entry.createdAt)}</time>
                </header>
                <p className="al-work-thread-body">{entry.body}</p>
                {entry.questionId && onJumpQuestion ? (
                  <button
                    type="button"
                    className="al-work-thread-jump"
                    onClick={() => onJumpQuestion(entry.questionId as string)}
                  >
                    Jump to question
                  </button>
                ) : null}
              </article>
            ),
          )
        )}
      </div>

      {canCompose ? (
        <div className="al-work-thread-compose">
          <textarea
            className="al-work-thread-input"
            rows={3}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={
              placeholder
                ? placeholder
                : audience === "personal"
                ? tab === "all"
                  ? "Note about this activity"
                  : "Note on this attempt"
                : audience === "staff"
                  ? tab === "all"
                    ? "Staff note about this activity"
                    : "Staff note on this attempt"
                  : tab === "all"
                    ? "A question about this activity"
                    : "Message on this attempt"
            }
            disabled={sending}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                void send();
              }
            }}
          />
          <button
            type="button"
            className="al-work-thread-send"
            disabled={sending || !draft.trim()}
            onClick={() => void send()}
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      ) : (
        <p className="al-work-thread-hint">
          {closedHint
            ? closedHint
            : data?.canReply === false
            ? channels
              ? "You cannot message the student on this course."
              : "Replies are off for this student."
            : "Open a handed-in attempt to write, or stay on All for a question about the activity."}
        </p>
      )}
    </div>
  );
}

export function WorkThreadTrigger({
  open,
  onClick,
}: {
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`al-grade-dock-btn${open ? " is-open" : ""}`}
      aria-expanded={open}
      aria-controls="al-work-thread-panel"
      onClick={onClick}
    >
      <MessageSquare size={16} aria-hidden="true" />
      <span>Chat</span>
    </button>
  );
}
